import { AXIS_MAX, Action, TICK_RATE, buildKit, type CharacterManifest, type FighterKit, type MoveSpec, type Placement } from "@arena/shared";

/**
 * Giovanna d'Arco (docs/personaggi/giovanna.md): a young woman of twenty in
 * gleaming plate armour a size too big, who hears voices and does what they
 * say. Heroic and a little ridiculous, never sexualised. A sword and the pole
 * of her banner; S1 plants the banner, which shields her and her teammates;
 * S2 is a dash the voices aim at the nearest enemy; the Super Slam puts her on
 * a white warhorse, faster and stronger in everything.
 */
export const GIOVANNA: CharacterManifest = {
  id: "giovanna",
  schemaVersion: 2,
  displayName: "Giovanna d'Arco",
  archetype: "mobile",
  description: "Vent'anni, un'armatura di una taglia in più e delle voci che le danno consigli tattici. Pianta lo stendardo per proteggere sé e i compagni, scatta sul nemico che le indicano i santi. Suprema: a cavallo, più veloce e più forte in tutto.",
  look: { build: "slim", accent: "#5b8fd6" },
  stats: { speed: 70, power: 50, resistance: 45, weight: 45, range: 55, slamCharge: 55 },
  moveset: {
    light1: { name: "Fendente", startup: 4, active: 2, recovery: 8, range: 1.6, arcDeg: 90, knockback: 2.5, lift: 0, hitstun: 13, slamGain: 6, lunge: 2.5, cancelFrom: 6, nextLight: Action.Light2, superCancel: true, specialCancel: true, damage: 6 },
    light2: { name: "Rovescio", startup: 4, active: 3, recovery: 9, range: 1.7, arcDeg: 120, knockback: 3, lift: 0, hitstun: 14, slamGain: 6, lunge: 2, cancelFrom: 7, nextLight: Action.Light3, superCancel: true, specialCancel: true, damage: 7 },
    light3: { name: "Per la Francia!", startup: 7, active: 3, recovery: 14, range: 2.4, arcDeg: 160, knockback: 8, lift: 3, hitstun: 20, slamGain: 10, lunge: 2, cancelFrom: 255, nextLight: Action.None, superCancel: true, damage: 11 },
    airAttack: { name: "Discesa angelica", startup: 5, active: 4, recovery: 11, range: 1.6, arcDeg: 360, knockback: 5, lift: 2, hitstun: 14, slamGain: 7, lunge: 0, cancelFrom: 12, nextLight: Action.None, superCancel: true, specialCancel: true, damage: 9 },
    dashAttack: { name: "Assalto a Orléans", startup: 4, active: 4, recovery: 12, range: 1.6, arcDeg: 110, knockback: 6, lift: 1.5, hitstun: 16, slamGain: 8, lunge: 5, travel: 8, cancelFrom: 10, nextLight: Action.None, superCancel: true, specialCancel: true, damage: 10 },
    special1: {
      name: "Lo stendardo",
      startup: 10, active: 1, recovery: 10, range: 0, arcDeg: 360, knockback: 0, lift: 0, hitstun: 0, slamGain: 0, lunge: 0,
      cancelFrom: 255, nextLight: Action.None, superCancel: true, damage: 0, cooldown: 14 * TICK_RATE, groundOnly: true,
    },
    special2: {
      name: "Me l'ha detto San Michele",
      // A rush that strikes all along the way; the voices point it (GIOVANNA_VOICES).
      startup: 3, active: 8, recovery: 12, range: 1.5, arcDeg: 120, knockback: 6, lift: 2, hitstun: 16, slamGain: 8, lunge: 0, travel: 18,
      cancelFrom: 255, nextLight: Action.None, superCancel: true, damage: 9, cooldown: 5 * TICK_RATE,
    },
    superSlam: {
      name: "La carica di Orléans",
      startup: 12, active: 1, recovery: 8, armorTicks: 12, range: 0, arcDeg: 360, knockback: 0, lift: 0, hitstun: 0, slamGain: 0, lunge: 0,
      cancelFrom: 255, nextLight: Action.None, superCancel: false, damage: 0,
    },
  },
};

/**
 * The banner (S1): planted where she stands for `ticks`, `radius` m round.
 * She and her teammates inside take `damageTaken` of the damage and
 * `knockbackTaken` of the push, and every `every` ticks of a match gain
 * `slam` of meter. One banner at a time.
 */
export const GIOVANNA_BANNER = { radius: 3, ticks: 6 * TICK_RATE, damageTaken: 0.7, knockbackTaken: 0.6, every: 15, slam: 2 } as const;

/** The voices (S2): the nearest enemy within `seek` m when the rush starts is where it goes. */
export const GIOVANNA_VOICES = { seek: 7 } as const;

/**
 * The stick that sends the rush at the enemy the voices point to: the
 * nearest of `enemies` within reach, or null (the rush keeps her own
 * direction). Client and bots use the same rule.
 */
export function voicesCommand(self: Placement, enemies: readonly Placement[]): { mx: number; mz: number } | null {
  let best: Placement | null = null;
  let bestDistance: number = GIOVANNA_VOICES.seek;
  for (const e of enemies) {
    if (Math.abs(e.y - self.y) > 2) continue;
    const d = Math.hypot(e.x - self.x, e.z - self.z);
    if (d > 1e-3 && d < bestDistance) {
      best = e;
      bestDistance = d;
    }
  }
  if (!best) return null;
  return { mx: Math.round(((best.x - self.x) / bestDistance) * AXIS_MAX), mz: Math.round(((best.z - self.z) / bestDistance) * AXIS_MAX) };
}

/**
 * On the white warhorse (Super Slam) for `ticks`: faster, higher jumps,
 * stronger and longer blows (`reach` m more), steadier under blows; in Slam
 * each enemy her hits reach scores once.
 */
export const GIOVANNA_HORSE = { ticks: 10 * TICK_RATE, speed: 1.5, dash: 1.3, jump: 1.3, damage: 1.25, knockback: 1.3, reach: 0.8 } as const;

const mounted = (move: MoveSpec): MoveSpec => ({
  ...move,
  damage: (move.damage ?? 0) * GIOVANNA_HORSE.damage,
  knockback: move.knockback * GIOVANNA_HORSE.knockback,
  range: move.range > 0 ? move.range + GIOVANNA_HORSE.reach : 0,
});
const horse = buildKit({
  ...GIOVANNA,
  moveset: Object.fromEntries(Object.entries(GIOVANNA.moveset).map(([k, m]) => [k, m && k !== "superSlam" ? mounted(m) : m])) as CharacterManifest["moveset"],
});
const onFoot = buildKit(GIOVANNA);
/** Her kit on horseback. */
export const GIOVANNA_HORSE_KIT: FighterKit = Object.freeze({
  ...horse,
  form: "horse" as const,
  maxSpeed: onFoot.maxSpeed * GIOVANNA_HORSE.speed,
  dashSpeed: onFoot.dashSpeed * GIOVANNA_HORSE.dash,
  jumpSpeed: onFoot.jumpSpeed * GIOVANNA_HORSE.jump,
  response: Object.freeze({ knockbackTaken: onFoot.response.knockbackTaken * 0.6, hitstunTaken: onFoot.response.hitstunTaken * 0.8 }),
});
